import {BaseElement, OSkinableInit} from "/@back@/commons/basis.js";
import {IItemUiEnv, installSrcViewFileDrop, newSrcView} from "/@back@/wsp/views/itemMain.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IView} from "/@lib@/commons/views.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {EditorView, highlightSpecialChars, keymap} from "/@res@/codemirror/view.js";
import {Compartment, EditorState, Extension} from "/@res@/codemirror/state.js";
import {lineNumbers} from "/@res@/codemirror/gutter.js";
import {foldGutter, foldKeymap} from "/@res@/codemirror/fold.js";
import {indentOnInput} from "/@res@/codemirror/language.js";
import {defaultHighlightStyle} from "/@res@/codemirror/highlight.js";
import {bracketMatching} from "/@res@/codemirror/matchbrackets.js";
import {closeBrackets} from "/@res@/codemirror/closebrackets.js";
import {autocompletion, completionKeymap} from "/@res@/codemirror/autocomplete.js";
import {defaultKeymap, defaultTabBinding} from "/@res@/codemirror/commands.js";
import {commentKeymap} from "/@res@/codemirror/comment.js";
import {defaultLangTypeProvider, getCmTheme, ILangTypeProvider} from "/@back@/edit/wed/wedlets/box/boxInputCode.js";
import {history, historyKeymap, redo, undo} from "/@res@/codemirror/history.js";
import {searchKeymap} from "/@res@/codemirror/search.js";
import {IDatasSrcView} from "/@lib@/wsp/wspMetaUi.js";
import {Resizer} from "/@back@/commons/widgets/resizer.js";
import {BarActions, OBarActionsInit} from "/@back@/commons/widgets/bars.js";
import {AccelKeyMgr, Action, IAction} from "/@lib@/commons/actions.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {createCmEditor, ICmEditor} from "/@lib@/wsp/wspsLiveCm.js";
import {srcUri} from "/@lib@/wsp/src.js";
import {LANG} from "/@lib@/commons/lang.js";
import {EWspWorkingSt, IWspsLivePlacePointer, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {ActionBtn} from "/@back@/commons/widgets/buttons.js";

/**
 * View ou éditeur du contenu de type CodeSource via codeMirror.
 */
export interface OItemCodeEdInit extends OSkinableInit, IWspsLivePlacePointer {
	reg?: IReg<IItemUiEnv>

	/** Pour une res dans un itemType folder. */
	srcUri?: string

	/** Langue à charger ou fournisseur dynamique de langue */
	langTypeDef?: string
	langTypeProvider?: ILangTypeProvider

	/** TODO actuellement TOUJOURS à TRUE. Si true, aucune édition possible, pas de barre d'édition. */
	noEdit?: boolean

	writePerms?: string | string[]

	/** Split de l'éditeur avec une autre vue de rendu. */
	renderView?: IDatasSrcView
	renderOrient?: `row` | 'column'
}

export interface ItemCodeEd extends BaseElement {
	initialize(init: OItemCodeEdInit): this
}

export class ItemCodeEd extends BaseElement implements IView {
	reg: IReg<IItemUiEnv>;
	place: WspsLivePlace;
	conf: OItemCodeEdInit;

	langTypeDef: string;
	langType: Extension | Promise<Extension> | null;

	accelKeyMgr?: AccelKeyMgr<ItemCodeEd>;

	cmEditor: ICmEditor;
	protected parentEd: HTMLElement;
	protected barActions: BarActions<ItemCodeEd>;
	saveBtn: HTMLElement;

	/** Affectation dynamique du langage. */
	setLangTypeDef(langTypeDef: string | null) {
		if (this.langTypeDef === langTypeDef) return;
		if (!langTypeDef) {
			this.langTypeDef = null;
			this.langType = null;
		} else {
			this.langTypeDef = langTypeDef;
			this.langType = this.conf.langTypeProvider ? this.conf.langTypeProvider(langTypeDef) || defaultLangTypeProvider(langTypeDef) : defaultLangTypeProvider(langTypeDef);
			if (this.langType instanceof Promise) this.langType.then((ext) => {this.langType = ext});
		}
		if (this.cmEditor) {
			if (this.langType instanceof Promise) {
				const langTypeDef = this.langTypeDef;
				this.langType.then(ext => {
					if (langTypeDef !== this.langTypeDef) return; //race cond.
					this.cmEditor.editorView.dispatch({effects: langTypeConf.reconfigure(ext)});
				});
			} else {
				this.cmEditor.editorView.dispatch({effects: langTypeConf.reconfigure(this.langType || [])});
			}
		}
	}

	/** */
	save() {
		this.cmEditor.save();
	}

	_initialize(init: OItemCodeEdInit) {
		this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.reg.installSkin('scroll/large', sr);
		installSrcViewFileDrop(this.reg, this);

		if (init.renderView) {
			this.parentEd = <div id="edParent" c-resizable=""/>;
			sr.append(
				this.parentEd,
				<Resizer c-orient={init.renderOrient || 'row'}/>,
			);
			newSrcView(init.renderView, this.reg).then((v) => {sr.appendChild(v)});
		}

		this.conf = init;
		this.place = init.place || this.reg.env.universe.wspServer.wspsLive.newPlace();
		this.setLangTypeDef(init.langTypeDef);

		// Listener pour les changements de roles (issus d'affectation de roles dans la hiérarchie ou de chgt de lcSt, resp...).
		this.reg.env.longDescChange.add((ldNew, ldOld) => {
			if (!LANG.arrayEquals(ldNew.srcRoles, ldOld.srcRoles)) this.refreshReadWriteState();
		});

		const options: Extension[] = [
			this.initLangType(),
			lineNumbers(),
			highlightSpecialChars(),
			foldGutter(),
			defaultHighlightStyle.fallback,
			EditorState.tabSize.of(2),
			//highlightSelectionMatches(), si on l'active, faudrait le recoder ou l'ajouter aussi coté XmlEd.
			getCmTheme(),
			keymap.of([
				...defaultKeymap,
				...foldKeymap,
			])
		];

		if (this.conf.noEdit) {
			options.push(EditorView.editable.of(false));
		} else {
			options.push(
				this.initReadWriteState(),
				history(),
				indentOnInput(),
				bracketMatching(),
				closeBrackets(),
				autocompletion(),
				keymap.of([
					...historyKeymap,
					...searchKeymap, //pas accessible en readonly dans CM...
					...commentKeymap,
					...completionKeymap,
					defaultTabBinding,
					// ...lintKeymap
				])
			);
			//Barre d'outils
			this.barActions = <BarActions î={{
				actions: this.reg.getList("actions:wsp-item-code-ed"),
				actionContext: this,
			} as OBarActionsInit<ItemCodeEd>}/> as BarActions<ItemCodeEd>;
			(this.parentEd || this.shadowRoot).appendChild(this.barActions);
			//Racourcis claviers
			const accels = this.reg.getListAsMap<IAction<ItemCodeEd>>("accelkeys:wsp-item-code-ed");
			if (accels) {
				this.accelKeyMgr = new AccelKeyMgr<ItemCodeEd>().initFromMapActions(accels);
				this.addEventListener('keydown', function (this: ItemCodeEd, ev: KeyboardEvent) {
					this.accelKeyMgr.handleKeyboardEvent(ev, this)
				});
			}
		}

		this.cmEditor = createCmEditor(this.getSrcUriToEdit(), this.reg, options, this.shadowRoot);
		this.cmEditor.contentEvents.on("onCtStateChange", (state, e) => {
			this.saveBtn?.classList.toggle("saving", state === "saving");
			this.barActions?.refreshContent();
		});
		this.cmEditor.contentEvents.on("onLock", this.refreshReadWriteState.bind(this));

		(this.parentEd || this.shadowRoot).appendChild(this.cmEditor.editorView.dom);
	}

	onViewShown() {
		this.place.setState(this.cmEditor.wspUri, EWspWorkingSt.r);
	}

	onViewHidden(closed?: boolean) {
		this.place.setState(this.cmEditor.wspUri, EWspWorkingSt.none);
		if (closed) this.cmEditor.close();
	}

	protected getSrcUriToEdit(): srcUri {
		return this.conf.srcUri || this.reg.env.itemType.getMainStreamSrcUri(this.reg.env.longDesc);
	}

	protected initLangType(): Extension {
		if (this.langType instanceof Promise) {
			const langTypeDef = this.langTypeDef;
			this.langType.then(ext => {
				if (langTypeDef !== this.langTypeDef) return; //race cond.
				this.cmEditor.editorView.dispatch({effects: langTypeConf.reconfigure(ext)});
			});
		} else if (this.langType != null) {
			return langTypeConf.of(this.langType);
		}
		return langTypeConf.of([]);
	}

	protected initReadWriteState(): Extension {
		return readOnlyConf.of(EditorView.editable.of(this.reg.hasPerm(this.writePermToCheck())));
	}

	refreshReadWriteState() {
		const writable = this.evalWritable();
		if (this.cmEditor.editorView.state.facet(EditorView.editable) !== writable) {
			this.cmEditor.editorView.dispatch({
				effects: readOnlyConf.reconfigure(EditorView.editable.of(writable))
			});
			this.barActions.refreshContent();
		}
	}

	evalWritable(): boolean {
		return this.cmEditor.lockedBy == null && this.reg.hasPerm(this.writePermToCheck());
	}

	protected writePermToCheck() {
		return this.conf.writePerms || "action.itemCodeEd#edit";
	}

}

REG.reg.registerSkin("wsp-item-code-ed", 1, /* language=CSS */ `
	:host,
	#edParent {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	c-resizer[c-orient=row] {
		width: 1px;
	}

	c-resizer[c-orient=column] {
		height: 1px;
	}

	c-bar-actions {
		border-bottom: 1px solid var(--border-color);
	}

	.saving {
		--icon-color: red;
	}

	.cm-editor {
		flex: 1;
		outline: var(--edit-caret-focus) !important;
		background-color: var(--edit-bgcolor) !important;
		overflow: auto !important;
	}

	.cm-scroller {
		overflow: auto;
		font-family: var(--font-mono, 'monospace') !important;
	}

	.cm-gutters,
	.cm-tooltip,
	.cm-foldPlaceholder {
		background-color: var(--bgcolor) !important;
		color: var(--fade-color) !important;
		border-color: var(--border-color) !important;
	}

	.cm-gutter {
		user-select: none;
	}

	.cm-searchMatch {
		background-color: var(--edit-search-bgcolor) !important;
		outline: none !important;
	}

	::selection {
		color: currentColor !important;
		background-color: var(--edit-seltext-bgcolor) !important;
	}
`);


customElements.define("wsp-item-code-ed", ItemCodeEd);


/** Compartment de config de la langue courante. */
const langTypeConf = new Compartment();

/** Compartment de config de l'état readOnly/writable du widget. */
const readOnlyConf = new Compartment();


function addAction(action: IAction<ItemCodeEd>, sort: number, accel?: string) {
	REG.reg.addToList("actions:wsp-item-code-ed", action.getId(), 1, action, sort);
	if (accel) REG.reg.addToList("accelkeys:wsp-item-code-ed", accel, 1, action);
}

addAction(new Action<ItemCodeEd>("save")
	.setLabel("Enregistrer")
	.setIcon("/@skin@/edit/wed/save.svg")
	.setVisible((ctx: ItemCodeEd) => ctx.cmEditor.lockedBy == null)
	.setEnabled((ctx: ItemCodeEd) => ctx.cmEditor.contentState === "dirty")
	.setExecute((ctx: ItemCodeEd) => {ctx.save()})
	.override('initButtonNode', (buttonNode: HTMLElement, ctx: ItemCodeEd) => {
		ctx.saveBtn = buttonNode;
	}), 10, "s-accel");


/** A voir à mutualiser avec LockAction de itemXmlEd via une fct custom lockedBy(). */
class LockAction extends Action<ItemCodeEd> {

	constructor() {
		super('lock');
		this.setIcon("/@skin@/wsp/widgets/itemEditor/editing.svg");
		this.setDescription("Ce contenu n'est pas éditable car il est actuellement en cours d'édition.");
	}

	isVisible(ctx: ItemCodeEd): boolean {
		return this.getLockedBy(ctx) != null;
	}

	getLabel(ctx: ItemCodeEd) {
		const lock = this.getLockedBy(ctx);
		return lock ? "Édité par " + lock.account : null;
	}

	initButtonNode(buttonNode: ActionBtn<ItemCodeEd>, ctx: ItemCodeEd) {
		buttonNode._hideLabel = false;
		buttonNode.tabIndexCustom = -2;
	}

	getLockedBy(ctx: ItemCodeEd) {
		return ctx.cmEditor.lockedBy;
	}
}

addAction(new LockAction(), 11);

addAction(new Action<ItemCodeEd>("undo")
	.setLabel("Annuler")
	.setIcon("/@skin@/edit/wed/undo.svg")
	.setExecute((ctx: ItemCodeEd) => {
		if (!undo(ctx.cmEditor.editorView)) POPUP.showNotifInfo("Aucune action à annuler", ctx);
	}), 20);

addAction(new Action<ItemCodeEd>("redo")
	.setLabel("Refaire")
	.setIcon("/@skin@/edit/wed/redo.svg")
	.setExecute((ctx: ItemCodeEd) => {
		if (!redo(ctx.cmEditor.editorView)) POPUP.showNotifInfo("Aucune action à refaire", ctx);
	}), 21);